import type { ComponentProps } from 'react';
import { Admin, CustomRoutes, Layout, Resource } from 'react-admin';
import { Route } from 'react-router-dom';

import { authProvider } from './admin/authProvider';
import { dataProvider } from './admin/dataProvider';
import { AppMenu } from './admin/AppMenu';
import { AppTopBar } from './admin/AppTopBar';
import { ExamNameProvider } from './admin/examName';
import { LoginPage } from './admin/LoginPage';
import { appTheme } from './admin/theme';
import { AudioGeneratePage } from './audioGenerate/AudioGeneratePage';
import {
    BlogPageCreate,
    BlogPageEdit,
    BlogPageList,
    BlogPageShow,
} from './blogPages/BlogPages';
import { BlogTagManagementPage } from './blogPages/BlogTagManagementPage';
import { BusinessOverviewPage } from './businessOverview/BusinessOverviewPage';
import { PatroniClusterPage } from './patroni/PatroniClusterPage';
import { PracticeTrendsPage } from './practiceTrends/PracticeTrendsPage';
import { PracticeHistoryAnalysisPage } from './practiceHistoryAnalysis/PracticeHistoryAnalysisPage';
import { ScoringObservabilityPage } from './scoringObservability/ScoringObservabilityPage';
import { UserRatingsPage } from './userRatings/UserRatingsPage';
import { UsageInsightsPage } from './usageInsights/UsageInsightsPage';
import { ClientSecurityAnalyticsPage } from './security/ClientSecurityAnalyticsPage';
import { PaymentOverviewPage } from './paymentOverview/PaymentOverviewPage';
import { BillingPage } from './billing/BillingPage';
import { ExamTaskDetailPage } from './examTasks/ExamTaskDetailPage';
import { ExamTaskTypePage } from './examTasks/ExamTaskTypePage';
import { MockTestDetailPage, MockTestManagementPage } from './mockTests/MockTestManagementPage';
import { MockTestGenerationProfilesPage } from './mockTests/MockTestGenerationProfilesPage';
import { MockTestScoreReviewDetailPage } from './mockTests/MockTestScoreReviewDetailPage';
import { MockTestScoreReviewPage } from './mockTests/MockTestScoreReviewPage';
import { MarkingConfigurationPage } from './markingConfiguration/MarkingConfigurationPage';
import { ProjectSettingsPage } from './settings/ProjectSettingsPage';
import { StaticContentsPage } from './settings/StaticContentsPage';
import { UserNotificationsPage } from './settings/UserNotificationsPage';
import { TemplateManagementPage } from './templates/TemplateManagementPage';
import { VocabularyManagementPage } from './templates/VocabularyManagementPage';
import { StudyPlanAlgorithmPage } from './studyPlanAlgorithm/StudyPlanAlgorithmPage';
import { SocialProofSettingsPage } from './socialProofSettings/SocialProofSettingsPage';
import { LiveExamTaskBulkFileSyncPage } from './examTasks/LiveExamTaskBulkFileSyncPage';
import { VideoGeneratePage } from './videoGenerate/VideoGeneratePage';
import { HomePageContentsPage } from './homePageContents/HomePageContentsPage';
import { QuickReadAloudAttemptsPage } from './quickReadAloud/QuickReadAloudAttemptsPage';
import {
    SpeechCalibrationAccess,
} from './speechCalibration/SpeechCalibrationAccess';
import {
    SPEECH_CALIBRATION_ADJUDICATOR_TAG,
    SPEECH_CALIBRATION_RATER_TAG,
    SPEECH_CALIBRATION_RELEASE_MANAGER_TAG,
} from './speechCalibration/speechCalibrationConstants';
import { SpeechCalibrationQueuePage } from './speechCalibration/SpeechCalibrationQueuePage';
import { SpeechCalibrationReviewPage } from './speechCalibration/SpeechCalibrationReviewPage';
import {
    SpeechCalibrationAdjudicationDetailPage,
    SpeechCalibrationAdjudicationPage,
} from './speechCalibration/SpeechCalibrationAdjudicationPages';
import { SpeechCalibrationReadinessPage } from './speechCalibration/SpeechCalibrationReadinessPage';
import { DiagnosisPage } from './questionDiagnosis/DiagnosisPage';
import { VoicePlaybackIssuesPage } from './voicePlaybackIssues/VoicePlaybackIssuesPage';
import { UltronKnowledgePage } from './ultronKnowledge/UltronKnowledgePage';
import { UltronInsightsPage } from './ultronInsights/UltronInsightsPage';
import { ADMIN_CONTACT_US_RESOURCE } from './admin/adminContactUsDataProvider';
import { ADMIN_FEEDBACK_RESOURCE } from './admin/adminFeedbackDataProvider';
import { ADMIN_AI_SCORE_FEEDBACK_RESOURCE } from './admin/adminAiScoreFeedbackDataProvider';
import {
    ContactUsEdit,
    ContactUsList,
    ContactUsShow,
} from './contactUs/ContactUsPages';
import {
    FeedbackEdit,
    FeedbackList,
    FeedbackShow,
} from './feedback/FeedbackPages';
import {
    AiScoreFeedbackEdit,
    AiScoreFeedbackList,
    AiScoreFeedbackShow,
} from './aiScoreFeedback/AiScoreFeedbackPages';
import { FlagReportPage } from './flagReports/FlagReportPage';
import { UserPhotoApprovalPage } from './support/UserPhotoApprovalPage';
import { ADMIN_USERS_RESOURCE } from './admin/adminUserDataProvider';
import {
    AdminUserCreate,
    AdminUserEdit,
    AdminUserList,
    AdminUserShow,
} from './users/AdminUserPages';

const AppLayout = (props: ComponentProps<typeof Layout>) => <Layout {...props} appBar={AppTopBar} menu={AppMenu} />;

export default function App() {
    // Hide review workflows in the admin
    const showReviewExamTasks = false;
    // Always show live/destination workflow management
    const showLiveExamTasks = true;
    // Only expose the bulk-sync page when running a production build
    const showSyncFiles = Boolean(import.meta.env.PROD);

    return (
        <ExamNameProvider>
            <Admin
                authProvider={authProvider}
                dashboard={BusinessOverviewPage}
                dataProvider={dataProvider}
                layout={AppLayout}
                loginPage={LoginPage}
                theme={appTheme}
            >
                <Resource
                    name="blogPages"
                    options={{ label: 'Blog Pages' }}
                    list={BlogPageList}
                    show={BlogPageShow}
                    create={BlogPageCreate}
                    edit={BlogPageEdit}
                />
                <Resource
                    name={ADMIN_CONTACT_US_RESOURCE}
                    options={{ label: 'Contact Us' }}
                    list={ContactUsList}
                    show={ContactUsShow}
                    edit={ContactUsEdit}
                />
                <Resource
                    name={ADMIN_FEEDBACK_RESOURCE}
                    options={{ label: 'Feedback' }}
                    list={FeedbackList}
                    show={FeedbackShow}
                    edit={FeedbackEdit}
                />
                <Resource
                    name={ADMIN_AI_SCORE_FEEDBACK_RESOURCE}
                    options={{ label: 'AI Score Feedback' }}
                    list={AiScoreFeedbackList}
                    show={AiScoreFeedbackShow}
                    edit={AiScoreFeedbackEdit}
                />
                <Resource
                    name={ADMIN_USERS_RESOURCE}
                    options={{ label: 'Users' }}
                    list={AdminUserList}
                    show={AdminUserShow}
                    create={AdminUserCreate}
                    edit={AdminUserEdit}
                />
                <CustomRoutes>
                    <Route path="/business-overview" element={<BusinessOverviewPage />} />
                    <Route path="/special-database" element={<PatroniClusterPage />} />
                    <Route path="/practice-trends" element={<PracticeTrendsPage />} />
                    <Route path="/practice-history-analysis" element={<PracticeHistoryAnalysisPage />} />
                    <Route path="/scoring-observability" element={<ScoringObservabilityPage />} />
                    <Route
                        path="/speech-calibration"
                        element={(
                            <SpeechCalibrationAccess requiredTags={[SPEECH_CALIBRATION_RATER_TAG]}>
                                <SpeechCalibrationQueuePage />
                            </SpeechCalibrationAccess>
                        )}
                    />
                    <Route
                        path="/speech-calibration/review/:assignmentId"
                        element={(
                            <SpeechCalibrationAccess requiredTags={[SPEECH_CALIBRATION_RATER_TAG]}>
                                <SpeechCalibrationReviewPage />
                            </SpeechCalibrationAccess>
                        )}
                    />
                    <Route
                        path="/speech-calibration/adjudication"
                        element={(
                            <SpeechCalibrationAccess requiredTags={[SPEECH_CALIBRATION_ADJUDICATOR_TAG]}>
                                <SpeechCalibrationAdjudicationPage />
                            </SpeechCalibrationAccess>
                        )}
                    />
                    <Route
                        path="/speech-calibration/adjudication/:adjudicationId"
                        element={(
                            <SpeechCalibrationAccess requiredTags={[SPEECH_CALIBRATION_ADJUDICATOR_TAG]}>
                                <SpeechCalibrationAdjudicationDetailPage />
                            </SpeechCalibrationAccess>
                        )}
                    />
                    <Route
                        path="/speech-calibration/readiness"
                        element={(
                            <SpeechCalibrationAccess requiredTags={[SPEECH_CALIBRATION_RELEASE_MANAGER_TAG]}>
                                <SpeechCalibrationReadinessPage />
                            </SpeechCalibrationAccess>
                        )}
                    />
                    <Route path="/user-ratings" element={<UserRatingsPage />} />
                    <Route path="/usage-insights" element={<UsageInsightsPage />} />
                    <Route path="/security" element={<ClientSecurityAnalyticsPage />} />
                    <Route path="/payment-overview" element={<PaymentOverviewPage />} />
                    <Route path="/blog-tags" element={<BlogTagManagementPage />} />
                    <Route path="/billing" element={<BillingPage />} />
                    <Route path="/billing/:section" element={<BillingPage />} />
                    <Route path="/mock-tests" element={<MockTestManagementPage />} />
                    <Route path="/question-diagnosis" element={<DiagnosisPage mode="questions" />} />
                    <Route path="/describe-image-preprocessing" element={<DiagnosisPage mode="describe-image-preprocessing" />} />
                    <Route path="/voice-playback-issues" element={<VoicePlaybackIssuesPage />} />
                    <Route path="/ultron-knowledge" element={<UltronKnowledgePage />} />
                    <Route path="/ultron-insights" element={<UltronInsightsPage />} />
                    <Route path="/mock-test-diagnosis" element={<DiagnosisPage mode="mock-tests" />} />
                    <Route path="/mock-test-generation-profiles" element={<MockTestGenerationProfilesPage />} />
                    <Route path="/mock-tests/:id" element={<MockTestDetailPage />} />
                    <Route path="/mock-test-score-review" element={<MockTestScoreReviewPage />} />
                    <Route path="/mock-test-score-review/:mockTestAttemptId" element={<MockTestScoreReviewDetailPage />} />
                    <Route path="/marking-configuration" element={<MarkingConfigurationPage />} />
                    <Route path="/marking-configuration/:section" element={<MarkingConfigurationPage />} />
                    <Route path="/flag-reports" element={<FlagReportPage />} />
                    <Route path="/photo-approval" element={<UserPhotoApprovalPage />} />
                    <Route path="/settings" element={<ProjectSettingsPage />} />
                    <Route path="/static-contents" element={<StaticContentsPage />} />
                    <Route path="/user-notifications" element={<UserNotificationsPage />} />
                    <Route path="/templates" element={<TemplateManagementPage />} />
                    <Route path="/vocabulary" element={<VocabularyManagementPage />} />
                    <Route path="/audio-generate" element={<AudioGeneratePage />} />
                    <Route path="/video-generate" element={<VideoGeneratePage />} />
                    <Route path="/home-page-contents" element={<HomePageContentsPage />} />
                    <Route path="/quick-read-aloud-attempts" element={<QuickReadAloudAttemptsPage />} />
                    <Route path="/study-plan-algorithm" element={<StudyPlanAlgorithmPage />} />
                    <Route path="/study-plan-algorithm/:section" element={<StudyPlanAlgorithmPage />} />
                    <Route path="/social-proof-settings" element={<SocialProofSettingsPage />} />
                    {showReviewExamTasks ? (
                        <>
                            <Route path="/exam-tasks/:type" element={<ExamTaskTypePage workflow="review" />} />
                            <Route path="/exam-tasks/:type/:id" element={<ExamTaskDetailPage workflow="review" />} />
                        </>
                    ) : null}

                    {showLiveExamTasks ? (
                        <>
                            <Route path="/live-exam-tasks/:type" element={<ExamTaskTypePage workflow="destination" />} />
                            <Route
                                path="/live-exam-task-by-id/:id"
                                element={<ExamTaskDetailPage workflow="destination" />}
                            />
                            <Route
                                path="/live-exam-tasks/:type/:id"
                                element={<ExamTaskDetailPage workflow="destination" />}
                            />
                        </>
                    ) : null}

                    {showSyncFiles ? (
                        <Route path="/live-exam-task-sync-files" element={<LiveExamTaskBulkFileSyncPage />} />
                    ) : null}
                </CustomRoutes>
            </Admin>
        </ExamNameProvider>
    );
}
